import { Component } from "react";
import { Col, Label, Row } from "reactstrap";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputTextarea } from "primereact/inputtextarea";
import { toastService } from "../../services/toastService";
import { featureFlagApiService } from "../../services/api/featureFlags/FeatureFlagApiService";
import { individualDocumentApiService } from "../../services/api/individual/IndividualDocumentApiService";
import { Result } from "../../models/Result";

// Same flag as the Documents tab (UserProfile, KycIndividualModal). CRM leaves the entitlement GET
// ungated on purpose (reads stay available), so a disabled CRM flag does not hide this panel by itself.
const MERCHANT_PORTAL_DOCUMENTS_FLAG = "merchantportal.additional_documents";

// No HTTP status = timeout or network failure: never show axios's raw text, and say the change may have landed.
const NO_RESPONSE_MESSAGE = "No response from the server. Reload the page and check before trying again.";
const errorMessage = (result: Result<unknown>): string => (result.status === undefined ? NO_RESPONSE_MESSAGE : result.message);

interface AdditionalCustomerDocumentsPanelProps {
    individualId: number;
    isActive: boolean;
}

interface AdditionalCustomerDocumentsPanelState {
    isAvailable: boolean;
    isLoading: boolean;
    loadFailed: boolean;
    allowDocumentUpload: boolean;
    isSavingEntitlement: boolean;
    isSendingNotification: boolean;
    notificationMessage: string;
    notificationIdempotencyKey: string;
}

export default class AdditionalCustomerDocumentsPanel extends Component<AdditionalCustomerDocumentsPanelProps, AdditionalCustomerDocumentsPanelState> {
    constructor(props: AdditionalCustomerDocumentsPanelProps) {
        super(props);

        this.state = {
            isAvailable: false,
            isLoading: true,
            loadFailed: false,
            allowDocumentUpload: false,
            isSavingEntitlement: false,
            isSendingNotification: false,
            notificationMessage: "",
            notificationIdempotencyKey: "",
        };
    }

    async componentDidMount() {
        if (!this.props.isActive || !(await featureFlagApiService.isEnabled(MERCHANT_PORTAL_DOCUMENTS_FLAG))) {
            this.setState({ isLoading: false });
            return;
        }

        await this.loadEntitlement();
    }

    // 403 (no permission) and 404 (endpoint not deployed) mean the panel does not apply to this operator
    // or environment, so it stays hidden. Any other failure is a real error and gets a Retry.
    loadEntitlement = async () => {
        this.setState({ isLoading: true, loadFailed: false });
        const result = await individualDocumentApiService.getUploadEntitlement(this.props.individualId);
        if (result.success) {
            this.setState({
                isAvailable: true,
                allowDocumentUpload: result.data.allowDocumentUpload,
                isLoading: false,
            });
            return;
        }

        this.setState({ isLoading: false, loadFailed: result.status !== 403 && result.status !== 404 });
    };

    onEntitlementChanged = async (allowDocumentUpload: boolean) => {
        this.setState({ isSavingEntitlement: true });
        const result = await individualDocumentApiService.updateUploadEntitlement(this.props.individualId, { allowDocumentUpload });
        this.setState({ isSavingEntitlement: false });

        if (!result.success) {
            toastService.error(errorMessage(result));
            return;
        }

        this.setState({ allowDocumentUpload: result.data.allowDocumentUpload });
        toastService.success(allowDocumentUpload ? "Additional document upload enabled." : "Additional document upload disabled.");
    };

    onNotifyCustomer = async () => {
        const message = this.state.notificationMessage.trim();
        if (!message) {
            toastService.error("Enter a message before sending the notification.");
            return;
        }

        const idempotencyKey = this.state.notificationIdempotencyKey
            || `additional-document-notification-${this.props.individualId}-${Date.now()}`;
        this.setState({
            isSendingNotification: true,
            notificationIdempotencyKey: idempotencyKey,
        });

        const result = await individualDocumentApiService.notifyCustomer(this.props.individualId, { message, idempotencyKey });
        this.setState({ isSendingNotification: false });

        if (!result.success) {
            toastService.error(errorMessage(result));
            return;
        }

        this.setState({ notificationIdempotencyKey: "" });
        toastService.success("Additional document upload notification sent.");
    };

    render() {
        if (this.state.loadFailed) {
            return (
                <div className="mb-4 flex align-items-center gap-2">
                    <Label style={{ marginBottom: 0 }}>Additional customer documents could not be loaded.</Label>
                    <Button label="Retry" type="button" size="small" outlined onClick={this.loadEntitlement} />
                </div>
            );
        }

        if (!this.props.isActive || this.state.isLoading || !this.state.isAvailable) {
            return null;
        }

        return (
            <div className="mb-4">
                <Label style={{ fontWeight: "bold", fontSize: "1.1em" }}>Additional Customer Documents</Label>
                <Row>
                    <Col className="p-2.5" sm={12} md={5} lg={4}>
                        <div className="flex align-items-center gap-2">
                            <Checkbox
                                inputId="allowAdditionalDocumentUpload"
                                checked={this.state.allowDocumentUpload}
                                disabled={this.state.isSavingEntitlement}
                                onChange={(event) => this.onEntitlementChanged(!!event.checked)}
                            />
                            <Label for="allowAdditionalDocumentUpload" style={{ marginBottom: 0 }}>
                                Allow customer to upload additional documents
                            </Label>
                        </div>
                    </Col>
                    <Col className="p-2.5" sm={12} md={7} lg={8}>
                        <Label for="additionalDocumentNotificationMessage">Customer notification</Label>
                        <div className="flex gap-2">
                            <InputTextarea
                                id="additionalDocumentNotificationMessage"
                                value={this.state.notificationMessage}
                                onChange={(event) => this.setState({ notificationMessage: event.target.value })}
                                rows={2}
                                autoResize
                                disabled={!this.state.allowDocumentUpload}
                                placeholder="Ask the customer to upload the required additional documents."
                            />
                            <Button
                                label="Notify customer"
                                type="button"
                                loading={this.state.isSendingNotification}
                                disabled={!this.state.allowDocumentUpload}
                                onClick={this.onNotifyCustomer}
                            />
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }
}
